import React from 'react';
import { Link } from 'react-router-dom';
import { BlogPostPreview as BlogPostPreviewType } from '../../types/blog';

interface BlogPostPreviewProps {
  post: BlogPostPreviewType;
}

const BlogPostPreview: React.FC<BlogPostPreviewProps> = ({ post }) => {
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - date.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) return 'Yesterday';
      if (diffDays <= 7) return `${diffDays} days ago`;
      
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Link 
      to={`/blog/${post.slug}`}
      className="block group"
    >
      <div className="border-l-4 border-blue-500 pl-4 py-3 hover:bg-gray-50 transition-colors duration-200">
        <div className="flex gap-3">
          {/* Featured Image Thumbnail */}
          {post.featuredImage && (
            <div className="flex-shrink-0">
              <img
                src={`${post.featuredImage.url}?w=80&h=60&fit=fill&f=center`}
                alt={post.featuredImage.alt}
                className="w-20 h-15 object-cover rounded border border-gray-200 group-hover:border-blue-300 transition-colors"
                onError={(e) => {
                  console.error('Image failed to load:', post.featuredImage?.url);
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          )}
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-gray-900 line-clamp-1 group-hover:text-blue-600">
              {post.title}
            </h3>
            
            <p className="text-sm text-gray-600 line-clamp-2 mt-1">
              {post.excerpt || 'No description available'}
            </p>
            
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
              <span>{formatDate(post.publishedDate)}</span>
              <span>·</span>
              <span>{post.readTime} min read</span>
              {post.likeCount !== undefined && (
                <>
                  <span>·</span>
                  <span className="flex items-center gap-1">
                    <svg 
                      className="w-3 h-3 text-red-500" 
                      fill="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    {post.likeCount}
                  </span>
                </>
              )}
              {post.isNew && (
                <>
                  <span>·</span>
                  <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                    New
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default BlogPostPreview;